/**
 * Estado transversal de la pestaña de asistencia: la sesión del portal y el
 * estado del módulo (habilitado, modo, bloqueos abiertos).
 *
 * Context propio, sin librería de estado (§2.3). El hook va en su propio
 * archivo (`useSesion.ts`) porque `react-refresh` exige que un módulo de
 * componentes exporte solo componentes.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { pedir, postear, type EstadoModulo, type Sesion } from '../api';
import { ContextoSesion, type ValorSesion } from './contextoSesion';

export function SesionProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [modulo, setModulo] = useState<EstadoModulo | null>(null);
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;

    void (async () => {
      try {
        const estado = await pedir<EstadoModulo>('/api/asistencia/estado');
        if (!vigente) return;
        setModulo(estado);

        if (estado.habilitado) {
          // Sin sesión el back responde 401: no es un error a mostrar, es el
          // caso normal de "todavía no ingresó". Con `sinLogin` la misma ruta
          // devuelve la sesión sintética, así que el flujo es el mismo.
          try {
            const s = await pedir<Sesion>('/api/asistencia/sesion');
            if (vigente) setSesion(s);
          } catch {
            if (vigente) setSesion(null);
          }
        }
      } catch (err) {
        if (vigente) {
          setModulo({
            habilitado: false,
            motivo: err instanceof Error ? err.message : 'No se pudo consultar el estado del módulo.',
          });
        }
      } finally {
        if (vigente) setCargando(false);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  const ingresar = useCallback(async (usuario: string, clave: string) => {
    setSesion(await postear<Sesion>('/api/asistencia/sesion', { usuario, clave }));
  }, []);

  /**
   * Relee la sesión del servidor.
   *
   * Hace falta después de cambiar la clave: lo que cambió —que ya no es
   * provisoria— lo sabe el back, y sin releer la pantalla seguiría pidiendo
   * el cambio para siempre.
   */
  const recargarSesion = useCallback(async () => {
    try {
      setSesion(await pedir<Sesion>('/api/asistencia/sesion'));
    } catch {
      setSesion(null);
    }
  }, []);

  const salir = useCallback(async () => {
    await pedir('/api/asistencia/sesion', { method: 'DELETE' });
    setSesion(null);
  }, []);

  const valor = useMemo<ValorSesion>(
    () => ({ modulo, sesion, cargando, ingresar, salir, recargarSesion }),
    [modulo, sesion, cargando, ingresar, salir, recargarSesion],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}
